import http from "@/helper/http";
import { RESOURCE_CACHE } from "@/config/queryConfig";

// Request deduplication cache
const pendingRequests = new Map();

// Data cache with timestamps
const dataCache = new Map();

// Expose cache for clearing (used by useCacheClear composable)
if (typeof window !== "undefined") {
  window.__VUEX_PURCHASES_CACHE__ = dataCache;
}

const state = {
  // Purchases
  purchases: [],
  loadingPurchases: false,

  // Pagination
  purchasesPagination: {
    page: 1,
    per_page: 20,
    total: 0,
    pages: 0,
  },

  // Filters
  purchasesFilters: {
    query: "",
    sort: "created_at",
  },

  // Error handling
  error: null,
};

const getters = {
  // Purchases
  purchases: (state) => state.purchases,
  loadingPurchases: (state) => state.loadingPurchases,
  purchasesPagination: (state) => state.purchasesPagination,
  purchasesFilters: (state) => state.purchasesFilters,

  // Errors
  error: (state) => state.error,
};

const mutations = {
  // Purchases
  SET_PURCHASES(state, purchases) {
    state.purchases = purchases;
  },
  SET_LOADING_PURCHASES(state, loading) {
    state.loadingPurchases = loading;
  },
  SET_PURCHASES_PAGINATION(state, pagination) {
    state.purchasesPagination = { ...state.purchasesPagination, ...pagination };
  },
  SET_PURCHASES_FILTERS(state, filters) {
    state.purchasesFilters = { ...state.purchasesFilters, ...filters };
  },

  // Error handling
  SET_ERROR(state, error) {
    state.error = error;
  },
  CLEAR_ERROR(state) {
    state.error = null;
  },
};

const actions = {
  // ==================== PURCHASES ====================

  async fetchPurchases(
    { commit, state },
    { storeId, filters = {}, page, perPage, forceRefresh = false } = {}
  ) {
    const pageNum = page || state.purchasesPagination.page;
    const perPageNum = perPage || state.purchasesPagination.per_page;
    const cacheKey = `purchases-${storeId}-${JSON.stringify(
      filters
    )}-${pageNum}-${perPageNum}`;

    // Check cache first (unless forceRefresh) - BEFORE setting loading state
    if (!forceRefresh && dataCache.has(cacheKey)) {
      const cached = dataCache.get(cacheKey);
      const age = Date.now() - cached.timestamp;
      const maxAge = RESOURCE_CACHE.purchases.staleTime;

      if (age < maxAge) {
        console.log(
          `✅ Using cached purchases (${Math.round(
            age / 1000
          )}s old) - No loading state`
        );
        // Don't set loading state for cached data
        commit("SET_PURCHASES", cached.data.items || []);
        commit("SET_PURCHASES_PAGINATION", {
          page: cached.data.page,
          per_page: cached.data.per_page,
          total: cached.data.total,
          pages: cached.data.pages,
        });
        return cached.data;
      } else {
        console.log(
          `♻️ Cache expired (${Math.round(
            age / 1000
          )}s old), fetching fresh data`
        );
        dataCache.delete(cacheKey);
      }
    }

    // Check if already fetching (deduplication)
    if (pendingRequests.has(cacheKey)) {
      console.log("⏭️ Deduplicating purchases request");
      return pendingRequests.get(cacheKey);
    }

    // Only set loading state when actually fetching
    commit("SET_LOADING_PURCHASES", true);
    commit("CLEAR_ERROR");

    const requestPromise = (async () => {
      try {
        const params = {
          page: pageNum,
          per_page: perPageNum,
          ...state.purchasesFilters,
          ...filters,
        };

        const response = await http.get(`/v2/store/${storeId}/purchases`, {
          params,
        });

        const data = response.data || {};

        // Cache the result
        dataCache.set(cacheKey, {
          data,
          timestamp: Date.now(),
        });

        commit("SET_PURCHASES", data.items || []);
        commit("SET_PURCHASES_PAGINATION", {
          page: data.page,
          per_page: data.per_page,
          total: data.total,
          pages: data.pages,
        });

        return data;
      } catch (error) {
        const message =
          error?.response?.data?.error?.message || "Failed to load purchases";
        commit("SET_ERROR", message);
        throw error;
      } finally {
        commit("SET_LOADING_PURCHASES", false);
        // Clean up pending request
        pendingRequests.delete(cacheKey);
      }
    })();

    // Store the promise for deduplication
    pendingRequests.set(cacheKey, requestPromise);

    return requestPromise;
  },

  // Update filters
  updatePurchasesFilters({ commit }, filters) {
    commit("SET_PURCHASES_FILTERS", filters);
  },

  // Clear cache
  clearPurchasesCache() {
    dataCache.clear();
    pendingRequests.clear();
    console.log("🗑️ Purchases cache cleared");
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
